import { ActiveComponent, SwitchConfig } from './phrases';
import {
  ContentPageSectionVisibility,
  PlannerConfigEntityPhraseStrings,
} from '@/services/configurator/PlannerConfigTypes';
import { BannerLargePreview } from '@/app/previewComponents/banner-large';
import { BannerMiniLinkPreview } from '@/app/previewComponents/banner-mini-link';
import { PlannerContentPagePreview } from '@/app/previewComponents/content-page/content-page';
import { Switch } from '@/components/ui/switch';
import { Label } from '@/components/ui/label';
import { useCallback, useMemo } from 'react';
import { ContentPageVariant } from '@pnpmworkspace/shared-types';

type PreviewModeProps = {
  previewMode: boolean;
  activeComponent: ActiveComponent;
  plannerUrl?: string;
  imageUrl?: string;
  phraseStrings?: PlannerConfigEntityPhraseStrings;
  secondaryBannerImgUrl?: string;
  contentPageVariant?: ContentPageVariant;
  contentPageVisible?: boolean;
  dynamicContentPageSectionVisibility?: ContentPageSectionVisibility;
  dynamicShowInPages: string[] | undefined;
  setActiveComponent: React.Dispatch<React.SetStateAction<ActiveComponent>>;
};

export const PreviewSwitchControl = ({
  config,
  activeComponent,
  onToggle,
  defaultSelectedComponent,
  className,
}: {
  config: SwitchConfig;
  activeComponent: ActiveComponent;
  onToggle: (component: ActiveComponent) => void;
  defaultSelectedComponent: boolean;
  className?: string;
}) => {
  if (!config.isVisible) {
    return null;
  }

  return (
    <div className={`flex items-center ${className}`}>
      <Switch
        id={config.id}
        checked={activeComponent === config.component}
        defaultChecked={defaultSelectedComponent}
        onCheckedChange={(checked) =>
          onToggle(checked ? config.component : null)
        }
        className='mr-2'
      />
      <Label htmlFor={config.id} className='mr-2'>
        {config.label}
      </Label>
    </div>
  );
};

export function PreviewMode(props: PreviewModeProps) {
  const {
    previewMode,
    activeComponent,
    plannerUrl,
    imageUrl,
    phraseStrings,
    secondaryBannerImgUrl,
    contentPageVariant,
    contentPageVisible,
    dynamicContentPageSectionVisibility,
    dynamicShowInPages,
    setActiveComponent,
  } = props;
  const handleToggle = useCallback((component: ActiveComponent) => {
    setActiveComponent(component);
  }, []);
  const switchConfigs: SwitchConfig[] = useMemo(() => {
    const showInPages = dynamicShowInPages || [];
    return [
      {
        id: 'banner-mini-visibility',
        label: 'Toggle Banner Mini',
        component: 'miniBanner',
        isVisible: previewMode && showInPages.includes('pip'),
      },
      {
        id: 'banner-large-visibility',
        label: 'Toggle Banner Large',
        component: 'largeBanner',
        isVisible:
          previewMode &&
          (showInPages.includes('pip') || showInPages.includes('plp')),
      },
      {
        id: 'content-page-visibility',
        label: 'Toggle Content Page',
        component: 'contentPage',
        isVisible: previewMode && !!contentPageVisible,
      },
    ];
  }, [previewMode, contentPageVisible, dynamicShowInPages]);
  return (
    <>
      {previewMode && (
        <>
          <div className='flex items-center flex-wrap gap-2'>
            {switchConfigs.map((config, index) => (
              <PreviewSwitchControl
                key={config.id}
                config={config}
                activeComponent={activeComponent}
                defaultSelectedComponent={index === 0}
                onToggle={handleToggle}
                className='mr-4'
              />
            ))}
          </div>
          <div className='mt-8'>
            {activeComponent === 'miniBanner' && (
              <BannerMiniLinkPreview
                phraseStrings={phraseStrings ?? {}}
                headerVisible={false}
                showInPages={props.dynamicShowInPages}
              />
            )}
            {activeComponent === 'largeBanner' && (
              <BannerLargePreview
                phraseStrings={phraseStrings ?? {}}
                imageUrl={imageUrl}
                headerVisible={false}
                showInPages={props.dynamicShowInPages}
              />
            )}
            {activeComponent === 'contentPage' && contentPageVisible && (
              <div className='w-1/2'>
                <PlannerContentPagePreview
                  contentPageVariant={contentPageVariant}
                  imageUrl={imageUrl}
                  phraseStrings={phraseStrings}
                  plannerUrl={plannerUrl}
                  secondaryBannerImgUrl={secondaryBannerImgUrl}
                  headerVisible={false}
                  dynamicView={dynamicContentPageSectionVisibility}
                  showInPages={props.dynamicShowInPages}
                />
              </div>
            )}
          </div>
        </>
      )}
    </>
  );
}
